import { Link, useLocation, useNavigate } from "react-router-dom";
import { Mail, Phone } from "lucide-react";
import Logo from "./Logo";
import { scrollToId } from "../lib/scroll";
import { trackEmailClick, trackPhoneClick } from "../lib/analytics";

const EXPLORE = [
    { label: "EOR Services", id: "services" },
    { label: "How It Works", id: "how-it-works" },
    { label: "US Business Solutions", id: "solutions" },
    { label: "About Hexagon", id: "about" },
    { label: "FAQ", id: "faq" },
    { label: "Contact", id: "contact" },
];

export default function Footer() {
    const location = useLocation();
    const navigate = useNavigate();

    const goTo = (id) => {
        if (location.pathname !== "/") {
            navigate("/", { state: { scrollTo: id } });
        } else {
            scrollToId(id);
        }
    };

    return (
        <footer data-testid="site-footer" className="bg-navy-deep text-white">
            <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 sm:px-8 lg:grid-cols-12 lg:py-20">
                <div className="lg:col-span-5">
                    <Logo dark />
                    <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/60">
                        Employer of Record and international employment
                        administration for businesses — with a primary focus on
                        the United States and additional service availability in
                        Canada.
                    </p>
                    <div className="mt-7 space-y-3">
                        <a
                            href="tel:+18333439367"
                            data-testid="footer-phone-link"
                            onClick={() => trackPhoneClick("footer")}
                            className="flex items-center gap-3 text-sm font-semibold text-white transition-colors duration-200 hover:text-gold"
                        >
                            <Phone size={16} className="text-gold" aria-hidden="true" />
                            +1 833-343-9367
                        </a>
                        <a
                            href="mailto:info@hexagoneor.com"
                            data-testid="footer-email-link"
                            onClick={() => trackEmailClick("footer")}
                            className="flex items-center gap-3 text-sm font-semibold text-white transition-colors duration-200 hover:text-gold"
                        >
                            <Mail size={16} className="text-gold" aria-hidden="true" />
                            info@hexagoneor.com
                        </a>
                    </div>
                </div>

                <nav aria-label="Footer" className="lg:col-span-4">
                    <p className="text-xs font-bold uppercase tracking-[0.22em] text-gold">
                        Explore
                    </p>
                    <ul className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3">
                        {EXPLORE.map((item) => (
                            <li key={item.id}>
                                <button
                                    type="button"
                                    data-testid={`footer-nav-${item.id}`}
                                    onClick={() => goTo(item.id)}
                                    className="text-sm text-white/65 transition-colors duration-200 hover:text-white"
                                >
                                    {item.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className="lg:col-span-3">
                    <p className="text-xs font-bold uppercase tracking-[0.22em] text-gold">
                        Legal
                    </p>
                    <ul className="mt-5 space-y-3">
                        <li>
                            <Link
                                to="/privacy-policy"
                                data-testid="footer-privacy-link"
                                className="text-sm text-white/65 transition-colors duration-200 hover:text-white"
                            >
                                Privacy Policy
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/terms-and-conditions"
                                data-testid="footer-terms-link"
                                className="text-sm text-white/65 transition-colors duration-200 hover:text-white"
                            >
                                Terms and Conditions
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="border-t border-white/10">
                <div className="mx-auto flex max-w-7xl flex-col gap-3 px-5 py-7 sm:px-8 lg:flex-row lg:items-center lg:justify-between">
                    <p className="text-xs text-white/45">
                        © 2026 Hexagon EOR. All rights reserved.
                    </p>
                    <p className="max-w-2xl text-xs leading-relaxed text-white/40">
                        Employer of record, employment administration, and
                        related legal and tax services are available in
                        supported markets only and are subject to a separate
                        service agreement. Immigration services are not
                        offered.
                    </p>
                </div>
            </div>
        </footer>
    );
}
